import React, { useState, useEffect, useCallback } from 'react';
import { TableCellsIcon, TrashIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { DndProvider, useDrag } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { classesApi, timetableApi, linesApi } from '../services/api';
import TimetableGrid from '../components/TimetableGrid';
import VersionControl from '../components/VersionControl';
import toast from 'react-hot-toast';
import type { Class, ClassAllocation } from '../types';

// Draggable class component
const DraggableClass: React.FC<{ classItem: Class; allocation: ClassAllocation }> = ({ classItem, allocation }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'class',
    item: { classItem },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  const isFullyAllocated = allocation.remaining_periods === 0;
  const progressPercentage = (allocation.allocated_periods / allocation.total_periods) * 100;

  return (
    <div
      ref={drag}
      className={`p-3 rounded-lg border cursor-move transition-all duration-200 ${
        isDragging ? 'opacity-50 scale-95' : 'opacity-100 scale-100'
      } ${
        isFullyAllocated 
          ? 'bg-green-100 border-green-300 text-green-800' 
          : 'bg-white border-neutral-silver text-neutral-dark-gray hover:bg-neutral-light-gray'
      }`}
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      <div className="font-montserrat font-semibold text-sm">{classItem.code}</div>
      <div className="text-xs text-neutral-medium-gray">Grade {classItem.grade}</div>
      <div className="text-xs text-neutral-medium-gray">{classItem.teacher1}</div>
      <div className="mt-2">
        <div className="flex justify-between text-xs">
          <span>{allocation.allocated_periods}/{allocation.total_periods}</span>
          <span>{Math.round(progressPercentage)}%</span>
        </div>
        <div className="w-full bg-neutral-light-gray rounded-full h-1.5 mt-1">
          <div 
            className={`h-1.5 rounded-full transition-all duration-300 ${
              isFullyAllocated ? 'bg-green-500' : 'bg-accent-teal'
            }`}
            style={{ width: `${progressPercentage}%` }}
          ></div>
        </div>
      </div>
    </div>
  );
};

// Draggable line component
const DraggableLine: React.FC<{ lineData: any }> = ({ lineData }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'line',
    item: { lineData },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  return (
    <div
      ref={drag}
      className={`p-3 rounded-lg border cursor-move transition-all duration-200 ${
        isDragging ? 'opacity-50 scale-95' : 'opacity-100 scale-100'
      } bg-white border-neutral-silver text-neutral-dark-gray hover:bg-neutral-light-gray`}
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      <div className="font-montserrat font-semibold text-sm">{lineData.code}</div>
      <div className="text-xs text-neutral-medium-gray">Grade {lineData.grade}</div>
      <div className="text-xs text-neutral-medium-gray">{lineData.class_codes_list.length} classes</div>
    </div>
  );
};

const TablePackingPage: React.FC = () => {
  const [classes, setClasses] = useState<Class[]>([]);
  const [selectedGrades, setSelectedGrades] = useState<number[]>([]);
  const [selectedMainTeacher, setSelectedMainTeacher] = useState<string>(''); // Main page filter
  const [selectedTeacher, setSelectedTeacher] = useState<string>(''); // Sidebar filter
  const [selectedClassCode, setSelectedClassCode] = useState<string>('');  
  const [selectedCurriculum, setSelectedCurriculum] = useState<string>(''); // Line curriculum filter
  const [packMode, setPackMode] = useState<'class' | 'line'>('class');
  const [lines, setLines] = useState<any[]>([]);
  const [showLineModal, setShowLineModal] = useState(false);
  const [selectedLine, setSelectedLine] = useState<any>(null);
  const [selectedSlotForLine, setSelectedSlotForLine] = useState<{day: number, period: number} | null>(null);
  const [selectedClassesForLine, setSelectedClassesForLine] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [allocations, setAllocations] = useState<{ [classCode: string]: ClassAllocation }>({});
  const [expandedGrades, setExpandedGrades] = useState<Set<number>>(new Set());
  const [hideFullClasses, setHideFullClasses] = useState(false);
  const [clashScanResult, setClashScanResult] = useState<any>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [showClashDetails, setShowClashDetails] = useState(false);
  const [expandedClashTypes, setExpandedClashTypes] = useState<Set<string>>(new Set());
  
  // Auto-Pack Modal State
  const [showAutoPackModal, setShowAutoPackModal] = useState(false);
  const [autoPackStep, setAutoPackStep] = useState<'info' | 'scanning' | 'result'>('info');
  const [autoPackProgress, setAutoPackProgress] = useState(0);
  const [autoPackInfo, setAutoPackInfo] = useState<any>(null);
  const [autoPackResult, setAutoPackResult] = useState<any>(null);
  const [autoPackMaxExplorations, setAutoPackMaxExplorations] = useState(10000); // Default 10k explorations
  const [autoPackSelectedGrades, setAutoPackSelectedGrades] = useState<number[]>([8, 9]); // Default: both grades
  const [showPartialConfirm, setShowPartialConfirm] = useState(false); // Confirmation for partial solution

  // Calculate total slots and packing progress
  const calculateSlotProgress = () => {
    const totalSlots = classes.reduce((sum, cls) => sum + cls.allocated_periods, 0);
    const packedSlots = Object.values(allocations).reduce((sum, allocation) => sum + allocation.allocated_periods, 0);
    const progressPercentage = totalSlots > 0 ? Math.round((packedSlots / totalSlots) * 100) : 0;
    
    return {
      totalSlots,
      packedSlots,
      progressPercentage
    };
  };

  // Calculate packing progress per grade with CAPS/CAM breakdown
  const calculatePackingProgressByGrade = () => {
    type ProgressData = { 
      packed: number; 
      total: number; 
      percentage: number;
      caps: { packed: number; total: number; percentage: number };
      cam: { packed: number; total: number; percentage: number };
    };
    const gradeProgress: { [grade: number]: ProgressData } = {};
    
    // Initialize progress for each grade
    grades.forEach(grade => {
      gradeProgress[grade] = { 
        packed: 0, 
        total: 0, 
        percentage: 0,
        caps: { packed: 0, total: 0, percentage: 0 },
        cam: { packed: 0, total: 0, percentage: 0 }
      };
    });
    
    // Calculate totals and packed slots per grade
    classes.forEach(cls => {
      const grade = cls.grade;
      if (grade && gradeProgress[grade]) {
        const isCam = /C-/.test(cls.code); // CAM classes have "C-" in their code
        const allocation = allocations[cls.code];
        const allocatedPeriods = allocation ? allocation.allocated_periods : 0;
        
        // Update overall totals
        gradeProgress[grade].total += cls.allocated_periods;
        gradeProgress[grade].packed += allocatedPeriods;
        
        // Update CAPS or CAM breakdown
        if (isCam) {
          gradeProgress[grade].cam.total += cls.allocated_periods;
          gradeProgress[grade].cam.packed += allocatedPeriods;
        } else {
          gradeProgress[grade].caps.total += cls.allocated_periods;
          gradeProgress[grade].caps.packed += allocatedPeriods;
        }
      }
    });
    
    // Calculate percentages
    Object.keys(gradeProgress).forEach(gradeKey => {
      const grade = parseInt(gradeKey);
      if (gradeProgress[grade].total > 0) {
        gradeProgress[grade].percentage = Math.round(
          (gradeProgress[grade].packed / gradeProgress[grade].total) * 100
        );
      }
      if (gradeProgress[grade].caps.total > 0) {
        gradeProgress[grade].caps.percentage = Math.round(
          (gradeProgress[grade].caps.packed / gradeProgress[grade].caps.total) * 100
        );
      }
      if (gradeProgress[grade].cam.total > 0) {
        gradeProgress[grade].cam.percentage = Math.round(
          (gradeProgress[grade].cam.packed / gradeProgress[grade].cam.total) * 100
        );
      }
    });
    
    return gradeProgress;
  };

  const { totalSlots, packedSlots, progressPercentage } = calculateSlotProgress();

  const loadLines = async () => {
    try {
      const response = await linesApi.getAll();
      // Process lines to ensure they have proper class_codes_list array and convert grades to numbers
      const processedLines = response.data.lines
        .map((line: any) => ({
          ...line,
          grade: typeof line.grade === 'string' ? parseInt(line.grade, 10) : line.grade, // Convert string grades to numbers
        }))
        .filter((line: any) => {
          // Filter out lines with invalid grades, null line codes, or 'nan' codes
          return line.code && line.code.trim() !== '' && 
                 line.code.toLowerCase() !== 'nan' &&
                 line.grade && !isNaN(line.grade) && typeof line.grade === 'number';
        });
      setLines(processedLines);
    } catch (error) {
      console.error('Error loading lines:', error);
      toast.error('Failed to load lines');
    }
  };

  const loadClasses = async () => {
    try {
      const response = await classesApi.getAll();
      setClasses(response.data);
    } catch (error) {
      console.error('Error loading classes:', error);
      toast.error('Failed to load classes');
    } finally {
      setLoading(false);
    }
  };



  const loadAllocations = useCallback(async () => {
    if (classes.length === 0) return;
    
    try {
      // Try to get current timetable first to count allocations
      const timetableResponse = await timetableApi.getCurrentTimetable();
      const currentSlots = timetableResponse.data;
      
      // Count allocated periods per class from current timetable
      const slotCounts: { [classCode: string]: number } = {};
      currentSlots.forEach((slot: any) => {
        // Count slots by class code (foreign key to classes.code)
        slotCounts[slot.code] = (slotCounts[slot.code] || 0) + 1;
      });
      
      // Build allocations map based on current timetable data
      const allocationsMap: { [classCode: string]: any } = {};
      classes.forEach(cls => {
        const allocatedCount = slotCounts[cls.code] || 0;
        allocationsMap[cls.code] = {
          allocated_periods: allocatedCount,
          total_periods: cls.allocated_periods,
          remaining_periods: cls.allocated_periods - allocatedCount
        };
      });
      
      setAllocations(allocationsMap);
    } catch (error) {
      console.error('Error loading allocations:', error);
      // Fallback: set default allocations
      const fallbackAllocations = classes.reduce((acc: any, cls: any) => {
        acc[cls.code] = {
          allocated_periods: 0,
          total_periods: cls.allocated_periods,
          remaining_periods: cls.allocated_periods
        };
        return acc;
      }, {});
      setAllocations(fallbackAllocations);
    }
  }, [classes]);

  useEffect(() => {
    loadClasses();
    loadLines();
  }, []);

  useEffect(() => {
    if (classes.length > 0) {
      loadAllocations();
    }
  }, [classes, refreshKey, loadAllocations]);

  const handleSlotUpdate = async () => {
    setRefreshKey(prev => prev + 1);
    toast.success('Timetable updated');
  };

  const handleVersionLoaded = () => {
    setRefreshKey(prev => prev + 1);
    toast.success('Version loaded successfully');
  };

  const handleClearTimetable = async () => {
    const confirmed = window.confirm(
      'Are you sure you want to clear the entire timetable? This action cannot be undone.'
    );
    
    if (confirmed) {
      try {
        console.log('Attempting to clear timetable...');
        const response = await timetableApi.clearTimetable();
        console.log('Clear response:', response);
        if (response.data && (response.data.success || response.data.deleted_count >= 0)) {
          toast.success(`Cleared ${response.data.deleted_count || 0} slots from timetable`);
          setRefreshKey(prev => prev + 1);
        } else {
          throw new Error('Clear operation failed');
        }
      } catch (error: any) {
        console.error('Error clearing timetable:', error);
        if (error.response && error.response.data) {
          toast.error(`Failed to clear timetable: ${error.response.data.detail || 'Unknown error'}`);
        } else {
          toast.error(`Failed to clear timetable: ${error.message || 'Unknown error'}`);
        }
      }
    }
  };

  const handleLinePack = async () => {
    if (!selectedLine || !selectedSlotForLine || selectedClassesForLine.length === 0) return;

    const { day, period } = selectedSlotForLine;
    const classesToPack = classes.filter(cls => selectedClassesForLine.includes(cls.code));
    
    // Check all classes for clashes first
    const clashChecks = [];
    for (const cls of classesToPack) {
      try {
        // Use new unified clash checking API
        const clashResponse = await timetableApi.checkClashUnified({
          day,
          period,
          code: cls.code,
          class_code: '', // Line types don't use class_code
          teacher1: cls.teacher1,
          teacher2: cls.teacher2 || undefined,
          room: undefined, // We'll get room from teacher data in backend
          type: 'line'  // Use 'line' type for clash detection
        });
        if (clashResponse.data.has_clash) {
          clashChecks.push({ class: cls, clash: clashResponse.data });
        }
      } catch (error) {
        console.error(`Error checking clash for ${cls.code}:`, error);
      }
    }
    
    // If any clashes, show error and don't pack
    if (clashChecks.length > 0) {
      const errorMessages = clashChecks.map(check => 
        `${check.class.code}: ${check.clash.clash_details.join(', ')}`
      ).join('\n');
      
      toast.error(`Cannot pack line. Clashes detected:\n${errorMessages}`);
      setShowLineModal(false);
      return;
    }
    
    // Pack all classes
    let successCount = 0;
    for (const cls of classesToPack) {
      try {
        await timetableApi.createSlot({
          day,
          period,
          code: cls.code,
          class_code: cls.class_code,
          subject_section: cls.subject_section,
          grade: cls.grade,
          teacher1_code: cls.teacher1,
          teacher2_code: cls.teacher2,
          subject_code: cls.subject_code,
          period_length: 1,
          type: 'line'  // Add the required type field for line packing
        });
        successCount++;
      } catch (error) {
        console.error(`Error packing ${cls.code}:`, error);
      }
    }
    
    if (successCount > 0) {
      toast.success(`Successfully packed ${successCount} classes from line ${selectedLine.code}`);
      handleSlotUpdate();
    }
    
    setShowLineModal(false);
  };

  // Helper function to get grade text colors
  const getGradeTextColor = (grade: number) => {
    const colors: { [key: number]: string } = {
      8: 'text-blue-600',
      9: 'text-green-600',
      10: 'text-yellow-600',
      11: 'text-purple-600',
      12: 'text-red-600',
    };
    return colors[grade] || 'text-gray-600';
  };

  const getGradeBarColor = (grade: number) => {
    const colors: { [key: number]: string } = {
      8: 'bg-blue-600',
      9: 'bg-green-600',
      10: 'bg-yellow-600',
      11: 'bg-purple-600',
      12: 'bg-red-600',
    };
    return colors[grade] || 'bg-gray-600';
  };

  const grades = Array.from(new Set([
    ...classes.map(cls => cls.grade),
    ...lines.map(line => line.grade)
  ])).filter(grade => !isNaN(grade) && grade !== null && grade !== undefined).sort((a, b) => a - b);

  // Calculate packing progress by grade (must be after grades is defined)
  const gradeProgress = calculatePackingProgressByGrade();

  // First filter by selected grades and main teacher filter (top level filters)
  const gradeFilteredClasses = classes.filter(cls => {
    const gradeMatch = selectedGrades.length === 0 || selectedGrades.includes(cls.grade);
    const mainTeacherMatch = !selectedMainTeacher || cls.teacher1 === selectedMainTeacher || cls.teacher2 === selectedMainTeacher;
    return gradeMatch && mainTeacherMatch;
  });
  
  // Only show classes that have a class_code from the grade-filtered classes
  const classSubjects = gradeFilteredClasses.filter(cls => cls.class_code && cls.class_code.trim() !== '');
  
  // Get unique teacher codes from the grade-filtered classes
  const teacherCodes = Array.from(new Set(
    classSubjects.flatMap(cls => [cls.teacher1, cls.teacher2].filter(t => t && t.trim() !== ''))
  )).sort();
  
  // Get unique class codes for filter from the grade-filtered class subjects
  const classCodes = Array.from(new Set(classSubjects.map(cls => cls.class_code)))
    .filter(classCode => classCode && classCode.trim() !== '' && classCode.toLowerCase() !== 'nan')
    .sort();
  
  // Filter lines by selected grades and curriculum type
  const filteredLines = lines.filter(line => {
    const gradeMatch = selectedGrades.length === 0 || selectedGrades.includes(line.grade);
    const curriculumMatch = !selectedCurriculum || line.curriculum_type === selectedCurriculum;
    return gradeMatch && curriculumMatch;
  });

  // Then filter by teacher and class code (sidebar level filters)
  const filteredClasses = classSubjects.filter(cls => {
    const teacherMatch = !selectedTeacher || cls.teacher1 === selectedTeacher || cls.teacher2 === selectedTeacher;
    const classCodeMatch = !selectedClassCode || cls.class_code === selectedClassCode;
    
    // Hide full classes filter
    const allocation = allocations[cls.code];
    const isFullyAllocated = allocation && allocation.remaining_periods === 0;
    const hideFullMatch = !hideFullClasses || !isFullyAllocated;
    
    return teacherMatch && classCodeMatch && hideFullMatch;
  });

  const handleClashScan = async () => {
    setIsScanning(true);
    try {
      const response = await timetableApi.independentClashScan();
      setClashScanResult(response.data);
      setShowClashDetails(false); // Reset details view
      
      if (!response.data.has_clashes) {
        toast.success('No clashes found! ✓');
      } else {
        toast.error(`Found ${response.data.total_clash_count} clash(es)`);
      }
    } catch (error) {
      console.error('Error scanning for clashes:', error);
      toast.error('Failed to scan for clashes');
    } finally {
      setIsScanning(false);
    }
  };

  // Auto-Pack Handlers
  const handleOpenAutoPack = async () => {
    setShowAutoPackModal(true);
    setAutoPackStep('info');
    try {
      const response = await timetableApi.getAutoPackInfo(autoPackSelectedGrades);
      setAutoPackInfo(response.data);
    } catch (error) {
      console.error('Error fetching auto-pack info:', error);
      toast.error('Failed to load auto-pack information');
    }
  };

  const handleAutoPackScan = async () => {
    setAutoPackStep('scanning');
    setAutoPackProgress(0);

    // Start timer
    const startTime = Date.now();
    const timerInterval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      setAutoPackProgress(elapsed);
    }, 100); // Update every 100ms for smooth display

    try {
      const response = await timetableApi.autoPackScan(autoPackSelectedGrades, autoPackMaxExplorations);
      clearInterval(timerInterval);
      setAutoPackResult(response.data);
      setAutoPackStep('result');
    } catch (error: any) {
      clearInterval(timerInterval);
      console.error('Error scanning for auto-pack solution:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to scan for solutions';
      toast.error(errorMsg);
      setAutoPackStep('info');
    }
  };

  const handleApplyAutoPack = async () => {
    try {
      await timetableApi.applyAutoPackSolution(autoPackSelectedGrades, autoPackMaxExplorations, false);
      toast.success('Auto-pack solution applied successfully!');
      setShowAutoPackModal(false);
      // Reset state
      setAutoPackStep('info');
      setAutoPackProgress(0);
      setAutoPackInfo(null);
      setAutoPackResult(null);
      setAutoPackMaxExplorations(10000);
      setAutoPackSelectedGrades([8, 9]); // Reset to both grades
      // Refresh timetable data
      setRefreshKey(prev => prev + 1)
    } catch (error) {
      console.error('Error applying auto-pack solution:', error);
      toast.error('Failed to apply solution');
    }
  };

  const handleApplyPartialSolution = async () => {
    try {
      await timetableApi.applyAutoPackSolution(autoPackSelectedGrades, autoPackMaxExplorations, true);
      toast.success('Partial solution applied successfully!');
      setShowAutoPackModal(false);
      setShowPartialConfirm(false);
      // Reset state
      setAutoPackStep('info');
      setAutoPackProgress(0);
      setAutoPackInfo(null);
      setAutoPackResult(null);
      setAutoPackMaxExplorations(10000);
      setAutoPackSelectedGrades([8, 9]); // Reset to both grades
      // Refresh timetable data
      setRefreshKey(prev => prev + 1)
    } catch (error) {
      console.error('Error applying partial solution:', error);
      toast.error('Failed to apply partial solution');
    }
  };

  const handleCloseAutoPack = () => {
    setShowAutoPackModal(false);
    setAutoPackStep('info');
    setAutoPackProgress(0);
    setAutoPackInfo(null);
    setAutoPackResult(null);
    setAutoPackMaxExplorations(10000);
    setAutoPackSelectedGrades([8, 9]); // Reset to default
  };

  // Re-fetch auto-pack info when selected grades change
  useEffect(() => {
    if (showAutoPackModal && autoPackStep === 'info' && autoPackSelectedGrades.length > 0) {
      const fetchInfo = async () => {
        try {
          const response = await timetableApi.getAutoPackInfo(autoPackSelectedGrades);
          setAutoPackInfo(response.data);
        } catch (error) {
          console.error('Error fetching auto-pack info:', error);
        }
      };
      fetchInfo();
    }
  }, [autoPackSelectedGrades, showAutoPackModal, autoPackStep]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-northwood-navy"></div>
      </div>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="space-y-6">
        {/* Header Section */}
        <div className="flex items-center space-x-3">
          <TableCellsIcon className="w-6 h-6 text-northwood-navy" />
          <h1 className="text-2xl font-montserrat font-semibold text-northwood-navy">Timetable Packing</h1>
        </div>

        {/* Mode Selector and Clash Scanner */}
        <div className="card">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-montserrat font-semibold text-neutral-dark-gray">Pack Mode:</span>
              <div className="flex items-center space-x-4">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    value="class"
                    checked={packMode === 'class'}
                    onChange={(e) => setPackMode(e.target.value as 'class' | 'line')}
                    className="text-northwood-navy"
                  />
                  <span className="text-sm">Pack Class</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    value="line"
                    checked={packMode === 'line'}
                    onChange={(e) => setPackMode(e.target.value as 'class' | 'line')}
                    className="text-northwood-navy"
                  />
                  <span className="text-sm">Pack Line</span>
                </label>
              </div>
            </div>

            {/* Clash Scanner Button */}
            <div className="flex items-center space-x-3">
              <span className="text-sm font-montserrat font-semibold text-neutral-dark-gray">Scan for clashes:</span>
              <button
                onClick={handleClashScan}
                disabled={isScanning}
                className={`px-4 py-2 rounded-lg font-montserrat font-semibold text-sm transition-all duration-200 ${
                  isScanning 
                    ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                    : clashScanResult === null
                    ? 'bg-northwood-navy text-white hover:bg-opacity-90'
                    : clashScanResult.has_clashes || (clashScanResult.unallocated_venue_count > 0)
                    ? 'bg-red-500 text-white hover:bg-red-600'
                    : 'bg-green-500 text-white hover:bg-green-600'
                }`}
              >
                {isScanning ? 'Scanning...' : clashScanResult === null ? 'Scan for Clashes' : clashScanResult.has_clashes || (clashScanResult.unallocated_venue_count > 0) ? `${clashScanResult.total_clash_count} Clash(es)${clashScanResult.unallocated_venue_count > 0 ? ` + ${clashScanResult.unallocated_venue_count} Unallocated` : ''}` : 'No Issues ✓'}
              </button>
            </div>
          </div>
        </div>

        {/* Clash Details Accordion */}
        {clashScanResult && clashScanResult.has_clashes && (
          <div className="card bg-red-50 border-2 border-red-300">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                <span className="font-montserrat font-semibold text-red-800">
                  {clashScanResult.total_clash_count} Clash(es) Detected
                </span>
              </div>
              <button
                onClick={() => setShowClashDetails(!showClashDetails)}
                className="text-red-700 hover:text-red-900 transition-colors"
              >
                {showClashDetails ? <ChevronUpIcon className="w-5 h-5" /> : <ChevronDownIcon className="w-5 h-5" />}
              </button>
            </div>

            {showClashDetails && (
              <div className="mt-4 space-y-3">
                {/* Teacher Clashes */}
                {clashScanResult.teacher_clashes && clashScanResult.teacher_clashes.length > 0 && (
                  <div className="border border-red-200 rounded-lg">
                    <button
                      onClick={() => {
                        const newExpanded = new Set(expandedClashTypes);
                        if (newExpanded.has('teacher')) {
                          newExpanded.delete('teacher');
                        } else {
                          newExpanded.add('teacher');
                        }
                        setExpandedClashTypes(newExpanded);
                      }}
                      className="w-full flex items-center justify-between p-3 bg-red-50 hover:bg-red-100 transition-colors rounded-t-lg"
                    >
                      <h3 className="font-montserrat font-semibold text-red-800">
                        Teacher Clashes ({clashScanResult.teacher_clashes.length})
                      </h3>
                      {expandedClashTypes.has('teacher') ? (
                        <ChevronUpIcon className="w-5 h-5 text-red-700" />
                      ) : (
                        <ChevronDownIcon className="w-5 h-5 text-red-700" />
                      )}
                    </button>
                    {expandedClashTypes.has('teacher') && (
                      <div className="p-3 space-y-2">
                        {clashScanResult.teacher_clashes.map((clash: any, index: number) => (
                          <div key={index} className="bg-white border border-red-200 rounded p-3">
                            <div className="font-semibold text-red-700">
                              Teacher: {clash.teacher_code} | Day {clash.day}, Period {clash.period}
                            </div>
                            <div className="mt-2 text-sm space-y-1">
                              {clash.conflicting_slots.map((slot: any, idx: number) => (
                                <div key={idx} className="text-gray-700">
                                  • {slot.code} (Grade {slot.grade}, Room: {slot.room || 'N/A'})
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Venue Clashes */}
                {clashScanResult.venue_clashes && clashScanResult.venue_clashes.length > 0 && (
                  <div className="border border-red-200 rounded-lg">
                    <button
                      onClick={() => {
                        const newExpanded = new Set(expandedClashTypes);
                        if (newExpanded.has('venue')) {
                          newExpanded.delete('venue');
                        } else {
                          newExpanded.add('venue');
                        }
                        setExpandedClashTypes(newExpanded);
                      }}
                      className="w-full flex items-center justify-between p-3 bg-red-50 hover:bg-red-100 transition-colors rounded-t-lg"
                    >
                      <h3 className="font-montserrat font-semibold text-red-800">
                        Venue Clashes ({clashScanResult.venue_clashes.length})
                      </h3>
                      {expandedClashTypes.has('venue') ? (
                        <ChevronUpIcon className="w-5 h-5 text-red-700" />
                      ) : (
                        <ChevronDownIcon className="w-5 h-5 text-red-700" />
                      )}
                    </button>
                    {expandedClashTypes.has('venue') && (
                      <div className="p-3 space-y-2">
                        {clashScanResult.venue_clashes.map((clash: any, index: number) => (
                          <div key={index} className="bg-white border border-red-200 rounded p-3">
                          <div className="font-semibold text-red-700">
                            Room: {clash.room} | Day {clash.day}, Period {clash.period}
                          </div>
                          <div className="mt-2 text-sm space-y-1">
                            {clash.conflicting_slots.map((slot: any, idx: number) => (
                              <div key={idx} className="text-gray-700">
                                • {slot.code} (Grade {slot.grade}, Teachers: {slot.teacher1_code}{slot.teacher2_code ? ` & ${slot.teacher2_code}` : ''})
                              </div>
                            ))}
                          </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Class Clashes */}
                {clashScanResult.class_clashes && clashScanResult.class_clashes.length > 0 && (
                  <div className="border border-red-200 rounded-lg">
                    <button
                      onClick={() => {
                        const newExpanded = new Set(expandedClashTypes);
                        if (newExpanded.has('class')) {
                          newExpanded.delete('class');
                        } else {
                          newExpanded.add('class');
                        }
                        setExpandedClashTypes(newExpanded);
                      }}
                      className="w-full flex items-center justify-between p-3 bg-red-50 hover:bg-red-100 transition-colors rounded-t-lg"
                    >
                      <h3 className="font-montserrat font-semibold text-red-800">
                        Class Clashes ({clashScanResult.class_clashes.length})
                      </h3>
                      {expandedClashTypes.has('class') ? (
                        <ChevronUpIcon className="w-5 h-5 text-red-700" />
                      ) : (
                        <ChevronDownIcon className="w-5 h-5 text-red-700" />
                      )}
                    </button>
                    {expandedClashTypes.has('class') && (
                      <div className="p-3 space-y-2">
                        {clashScanResult.class_clashes.map((clash: any, index: number) => (
                          <div key={index} className="bg-white border border-red-200 rounded p-3">
                          <div className="font-semibold text-red-700">
                            Class: {clash.class_code} | Day {clash.day}, Period {clash.period}
                          </div>
                          <div className="mt-2 text-sm space-y-1">
                            {clash.conflicting_slots.map((slot: any, idx: number) => (
                              <div key={idx} className="text-gray-700">
                                • {slot.code} (Grade {slot.grade}, Subject: {slot.subject_code}, Room: {slot.room || 'N/A'})
                              </div>
                            ))}
                          </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Line-Class Overlaps */}
                {clashScanResult.line_class_overlaps && clashScanResult.line_class_overlaps.length > 0 && (
                  <div className="border border-red-200 rounded-lg">
                    <button
                      onClick={() => {
                        const newExpanded = new Set(expandedClashTypes);
                        if (newExpanded.has('line_overlap')) {
                          newExpanded.delete('line_overlap');
                        } else {
                          newExpanded.add('line_overlap');
                        }
                        setExpandedClashTypes(newExpanded);
                      }}
                      className="w-full flex items-center justify-between p-3 bg-red-50 hover:bg-red-100 transition-colors rounded-t-lg"
                    >
                      <h3 className="font-montserrat font-semibold text-red-800">
                        Line-Class Overlaps ({clashScanResult.line_class_overlaps.length})
                      </h3>
                      {expandedClashTypes.has('line_overlap') ? (
                        <ChevronUpIcon className="w-5 h-5 text-red-700" />
                      ) : (
                        <ChevronDownIcon className="w-5 h-5 text-red-700" />
                      )}
                    </button>
                    {expandedClashTypes.has('line_overlap') && (
                      <div className="p-3 space-y-2">
                        {clashScanResult.line_class_overlaps.map((clash: any, index: number) => (
                          <div key={index} className="bg-white border border-red-200 rounded p-3">
                          <div className="font-semibold text-red-700">
                            {clash.line_code} | Day {clash.day}, Period {clash.period}
                          </div>
                          <div className="mt-2 text-sm">
                            <div className="text-purple-700 font-medium mb-1">Line Classes (placed):</div>
                            {clash.line_slots.map((slot: any, idx: number) => (
                              <div key={idx} className="text-gray-700 ml-2">
                                • {slot.code} (Room: {slot.room || 'N/A'})
                              </div>
                            ))}
                            <div className="text-orange-700 font-medium mt-2 mb-1">
                              Conflicting Base Classes: {clash.affected_base_classes.join(', ')}
                            </div>
                            {clash.base_class_slots.map((slot: any, idx: number) => (
                              <div key={idx} className="text-gray-700 ml-2">
                                • {slot.code} - {slot.class_code} (Subject: {slot.subject_code}, Room: {slot.room || 'N/A'})
                              </div>
                            ))}
                          </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* PE Clashes */}
                {clashScanResult.pe_clashes && clashScanResult.pe_clashes.length > 0 && (
                  <div className="border border-red-200 rounded-lg">
                    <button
                      onClick={() => {
                        const newExpanded = new Set(expandedClashTypes);
                        if (newExpanded.has('pe')) {
                          newExpanded.delete('pe');
                        } else {
                          newExpanded.add('pe');
                        }
                        setExpandedClashTypes(newExpanded);
                      }}
                      className="w-full flex items-center justify-between p-3 bg-red-50 hover:bg-red-100 transition-colors rounded-t-lg"
                    >
                      <h3 className="font-montserrat font-semibold text-red-800">
                        PE Clashes ({clashScanResult.pe_clashes.length})
                      </h3>
                      {expandedClashTypes.has('pe') ? (
                        <ChevronUpIcon className="w-5 h-5 text-red-700" />
                      ) : (
                        <ChevronDownIcon className="w-5 h-5 text-red-700" />
                      )}
                    </button>
                    {expandedClashTypes.has('pe') && (
                      <div className="p-3 space-y-2">
                        {clashScanResult.pe_clashes.map((clash: any, index: number) => (
                          <div key={index} className="bg-white border border-red-200 rounded p-3">
                          <div className="font-semibold text-red-700">
                            Grade {clash.grade} - {clash.class_code} | Day {clash.day}, Period {clash.period}
                          </div>
                          <div className="mt-1 text-sm text-gray-700">
                            {clash.subject_code} • Teacher: {clash.teacher}
                          </div>
                          <div className="mt-1 text-xs text-red-600">
                            {clash.message}
                          </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Leadership Clashes */}
                {clashScanResult.leadership_clashes && clashScanResult.leadership_clashes.length > 0 && (
                  <div className="border border-red-200 rounded-lg">
                    <button
                      onClick={() => {
                        const newExpanded = new Set(expandedClashTypes);
                        if (newExpanded.has('leadership')) {
                          newExpanded.delete('leadership');
                        } else {
                          newExpanded.add('leadership');
                        }
                        setExpandedClashTypes(newExpanded);
                      }}
                      className="w-full flex items-center justify-between p-3 bg-red-50 hover:bg-red-100 transition-colors rounded-t-lg"
                    >
                      <h3 className="font-montserrat font-semibold text-red-800">
                        Leadership Clashes ({clashScanResult.leadership_clashes.length})
                      </h3>
                      {expandedClashTypes.has('leadership') ? (
                        <ChevronUpIcon className="w-5 h-5 text-red-700" />
                      ) : (
                        <ChevronDownIcon className="w-5 h-5 text-red-700" />
                      )}
                    </button>
                    {expandedClashTypes.has('leadership') && (
                      <div className="p-3 space-y-2">
                        {clashScanResult.leadership_clashes.map((clash: any, index: number) => (
                          <div key={index} className="bg-white border border-red-200 rounded p-3">
                          <div className="font-semibold text-red-700">
                            Grade {clash.grade} - {clash.class_code} | Day {clash.day}, Period {clash.period}
                          </div>
                          <div className="mt-1 text-sm text-gray-700">
                            {clash.subject_code} • Teacher: {clash.teacher}
                          </div>
                          <div className="mt-1 text-xs text-red-600">
                            {clash.message}
                          </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Unallocated Venues */}
                {clashScanResult.unallocated_venues && clashScanResult.unallocated_venues.length > 0 && (
                  <div className="border border-orange-200 rounded-lg">
                    <button
                      onClick={() => {
                        const newExpanded = new Set(expandedClashTypes);
                        if (newExpanded.has('unallocated')) {
                          newExpanded.delete('unallocated');
                        } else {
                          newExpanded.add('unallocated');
                        }
                        setExpandedClashTypes(newExpanded);
                      }}
                      className="w-full flex items-center justify-between p-3 bg-orange-50 hover:bg-orange-100 transition-colors rounded-t-lg"
                    >
                      <h3 className="font-montserrat font-semibold text-orange-800">
                        Unallocated Venues ({clashScanResult.unallocated_venue_count})
                      </h3>
                      {expandedClashTypes.has('unallocated') ? (
                        <ChevronUpIcon className="w-5 h-5 text-orange-700" />
                      ) : (
                        <ChevronDownIcon className="w-5 h-5 text-orange-700" />
                      )}
                    </button>
                    {expandedClashTypes.has('unallocated') && (
                      <div className="p-3 space-y-2">
                        {clashScanResult.unallocated_venues.map((venue: any, index: number) => (
                          <div key={index} className="bg-white border border-orange-200 rounded p-3">
                          <div className="font-semibold text-orange-700">
                            {venue.code} {venue.class_code ? `(${venue.class_code})` : ''} | Day {venue.day}, Period {venue.period}
                          </div>
                          <div className="mt-1 text-sm text-gray-700">
                            Grade {venue.grade} • {venue.subject_code || 'N/A'} • Teachers: {venue.teacher1_code}{venue.teacher2_code ? ` & ${venue.teacher2_code}` : ''}
                          </div>
                          <div className="mt-1 text-xs text-orange-600 font-semibold">
                            Venue: FLOAT (Unallocated)
                          </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Controls */}
        {packMode === 'class' && (
          <div className="flex gap-4">
            {/* Grade Filter Card */}
            <div className="card flex-1">
              <div className="py-3">
                <div className="flex items-center space-x-3">
                  <label className="text-xs font-montserrat font-semibold text-neutral-dark-gray">Filter by Grade:</label>
                <div className="flex items-center space-x-2">
                  {grades.map(grade => (
                    <label key={grade} className="flex items-center space-x-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedGrades.includes(grade)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedGrades(prev => [...prev, grade]);
                          } else {
                            setSelectedGrades(prev => prev.filter(g => g !== grade));
                          }
                        }}
                        className="w-3 h-3 text-accent-teal border-neutral-silver rounded focus:ring-accent-teal focus:ring-1"
                      />
                      <span className={`text-xs font-medium ${getGradeTextColor(grade)}`}>Grade {grade}</span>
                    </label>
                  ))}
                  {selectedGrades.length > 0 && (
                    <button
                      onClick={() => setSelectedGrades([])}
                      className="text-xs text-gray-500 hover:text-gray-700 underline ml-1"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Teacher Filter Card */}
          <div className="card">
            <div className="py-3 px-4">
              <div className="flex items-center space-x-2">
                <label className="text-xs font-montserrat font-semibold text-neutral-dark-gray whitespace-nowrap">Teacher:</label>
                <select
                  value={selectedMainTeacher}
                  onChange={(e) => setSelectedMainTeacher(e.target.value)}
                  className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-accent-teal"
                >
                  <option value="">All Teachers</option>
                  {teacherCodes.map(teacherCode => (
                    <option key={teacherCode} value={teacherCode}>{teacherCode}</option>
                  ))}
                </select>
                {selectedMainTeacher && (
                  <button
                    onClick={() => setSelectedMainTeacher('')}
                    className="text-xs text-gray-500 hover:text-gray-700 underline"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Class Code Filter Card (Grade 8 & 9 only) */}
          <div className="card">
            <div className="py-3 px-4">
              <div className="flex items-center space-x-2">
                <label className="text-xs font-montserrat font-semibold text-neutral-dark-gray whitespace-nowrap">Class Code:</label>
                <select
                  value={selectedClassCode}
                  onChange={(e) => setSelectedClassCode(e.target.value)}
                  className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-accent-teal"
                >
                  <option value="">All Classes</option>
                  <optgroup label="Grade 8">
                    {['8A', '8B', '8C', '8D', '8E', '8F', '8G', '8H', '8I', '8X', '8Y'].map(code => (
                      <option key={code} value={code}>{code}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Grade 9">
                    {['9A', '9B', '9C', '9D', '9E', '9F', '9G', '9H', '9I', '9X', '9Y'].map(code => (
                      <option key={code} value={code}>{code}</option>
                    ))}
                  </optgroup>
                </select>
                {selectedClassCode && (
                  <button
                    onClick={() => setSelectedClassCode('')}
                    className="text-xs text-gray-500 hover:text-gray-700 underline"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Slot Progress Card */}
          <div className="card">
            <div className="py-3 px-4">
              <div className="text-center">
                <div className="text-xs font-medium text-neutral-dark-gray mb-2">
                  Slot Progress
                </div>
                <div className="flex items-center justify-center space-x-2">
                  <span className="text-xs text-neutral-medium-gray">
                    {packedSlots} / {totalSlots}
                  </span>
                  <div className="w-16 bg-neutral-light-gray rounded-full h-1.5">
                    <div 
                      className="bg-accent-teal h-1.5 rounded-full transition-all duration-300"
                      style={{ width: `${progressPercentage}%` }}
                    ></div>
                  </div>
                  <span className="text-xs text-neutral-medium-gray">
                    {progressPercentage}%
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Clear Timetable Card */}
          <div className="card">
            <div className="py-3">
              <button
                onClick={handleClearTimetable}
                className="btn-outline flex items-center space-x-1 text-red-600 border-red-300 hover:bg-red-50 text-xs px-3 py-1"
              >
                <TrashIcon className="w-4 h-4" />
                <span>Clear Timetable</span>
              </button>
            </div>
          </div>
        </div>
        )}
        
        {packMode === 'line' && (
          <div className="flex gap-4">
            {/* Grade Filter Card for Lines */}
            <div className="card flex-1">
              <div className="py-3">
                <div className="flex items-center space-x-3">
                  <label className="text-xs font-montserrat font-semibold text-neutral-dark-gray">Filter by Grade:</label>
                <div className="flex items-center space-x-2">
                  {grades.map(grade => (
                    <label key={grade} className="flex items-center space-x-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedGrades.includes(grade)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedGrades(prev => [...prev, grade]);
                          } else {
                            setSelectedGrades(prev => prev.filter(g => g !== grade));
                          }
                        }}
                        className="w-3 h-3 text-accent-teal border-neutral-silver rounded focus:ring-accent-teal focus:ring-1"
                      />
                      <span className={`text-xs font-medium ${getGradeTextColor(grade)}`}>Grade {grade}</span>
                    </label>
                  ))}
                  {selectedGrades.length > 0 && (
                    <button
                      onClick={() => setSelectedGrades([])}
                      className="text-xs text-gray-500 hover:text-gray-700 underline ml-1"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

            {/* Slot Progress Card */}
            <div className="card">
              <div className="py-3 px-4">
                <div className="text-center">
                  <div className="text-xs font-medium text-neutral-dark-gray mb-2">
                    Slot Progress
                  </div>
                  <div className="flex items-center justify-center space-x-2">
                    <span className="text-xs text-neutral-medium-gray">
                      {packedSlots} / {totalSlots}
                    </span>
                    <div className="w-16 bg-neutral-light-gray rounded-full h-1.5">
                      <div 
                        className="bg-accent-teal h-1.5 rounded-full transition-all duration-300"
                        style={{ width: `${progressPercentage}%` }}
                      ></div>
                    </div>
                    <span className="text-xs text-neutral-medium-gray">
                      {progressPercentage}%
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Clear Timetable Card */}
            <div className="card">
              <div className="py-3">
                <button
                  onClick={handleClearTimetable}
                  className="btn-outline flex items-center space-x-1 text-red-600 border-red-300 hover:bg-red-50 text-xs px-3 py-1"
                >
                  <TrashIcon className="w-4 h-4" />
                  <span>Clear Timetable</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Main Content Layout - Three columns aligned */}
        <div className="flex gap-6">
          {/* Left Sidebar - Classes or Lines */}
          <div className="w-48">
            <div className="card h-full">
              {packMode === 'class' && (
                <>
                  <div className="mb-4">
                    <h3 className="text-h3 font-montserrat text-northwood-navy mb-2">
                      Class Subjects ({filteredClasses.length})
                    </h3>
                  </div>
                  
                  {/* Filter Controls */}
                  <div className="space-y-3 mb-4 p-3 bg-gray-50 rounded-lg">
                    <div>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={hideFullClasses}
                          onChange={(e) => setHideFullClasses(e.target.checked)}
                          className="w-3.5 h-3.5 text-accent-teal border-gray-300 rounded focus:ring-accent-teal focus:ring-1"
                        />
                        <span className="text-xs font-medium text-gray-700">Hide Full Classes</span>
                      </label>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Teacher</label>
                      <select
                        value={selectedTeacher}
                        onChange={(e) => setSelectedTeacher(e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-accent-teal"
                      >
                        <option value="">All Teachers</option>
                        {teacherCodes.map(teacherCode => (
                          <option key={teacherCode} value={teacherCode}>{teacherCode}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Class Code</label>
                      <select
                        value={selectedClassCode}
                        onChange={(e) => setSelectedClassCode(e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-accent-teal"
                      >
                        <option value="">All Class Codes</option>
                        {classCodes.map(classCode => (
                          <option key={classCode} value={classCode}>{classCode}</option>
                        ))}
                      </select>
                    </div>
                    {(selectedTeacher || selectedClassCode) && (
                      <button
                        onClick={() => {
                          setSelectedTeacher('');
                          setSelectedClassCode('');
                        }}
                        className="w-full px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
                      >
                        Clear Filters
                      </button>
                    )}
                  </div>
                  
                  <div className="space-y-3 max-h-[500px] overflow-y-auto">
                    {classes.length > 0 ? (
                      filteredClasses.map((classItem) => (
                        <DraggableClass
                          key={classItem.code}
                          classItem={classItem}
                          allocation={allocations[classItem.code] || { allocated_periods: 0, total_periods: classItem.allocated_periods, remaining_periods: classItem.allocated_periods }}
                        />
                      ))
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <p className="text-sm">No classes found</p>
                        <p className="text-xs">Upload classes in the Inputs section</p>
                      </div>
                    )}
                  </div>
                </>
              )}

              {packMode === 'line' && (
                <>
                  <div className="mb-4">
                    <h3 className="text-h3 font-montserrat text-northwood-navy mb-2">
                      Subject Lines ({filteredLines.length})
                    </h3>
                  </div>
                  
                  {/* Curriculum Filter */}
                  <div className="space-y-3 mb-4 p-3 bg-gray-50 rounded-lg">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Curriculum</label>
                      <select
                        value={selectedCurriculum}
                        onChange={(e) => setSelectedCurriculum(e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-accent-teal"
                      >
                        <option value="">All Curriculums</option>
                        <option value="CAPS">CAPS Only</option>
                        <option value="Cambridge">Cambridge Only</option>
                      </select>
                    </div>
                    {selectedCurriculum && (
                      <button
                        onClick={() => setSelectedCurriculum('')}
                        className="w-full px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
                      >
                        Clear Filter
                      </button>
                    )}
                  </div>
                  
                  <div className="space-y-3 max-h-[500px] overflow-y-auto">
                    {filteredLines.length > 0 ? (
                      filteredLines.map((lineItem) => (
                        <DraggableLine
                          key={lineItem.code}
                          lineData={lineItem}
                        />
                      ))
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <p className="text-sm">No lines found</p>
                        <p className="text-xs">Upload classes in the Inputs section</p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Center - Timetable Grid */}
          <div className="flex-1">
            {classes.length > 0 ? (
              <TimetableGrid 
                key={refreshKey}
                classes={classes}
                selectedGrade={selectedGrades.length === 1 ? selectedGrades[0] : null}
                selectedTeacher={selectedMainTeacher}
                selectedClassCode={selectedClassCode}
                onSlotUpdate={handleSlotUpdate}
                onLineDrop={(line, day, period) => {
                  setSelectedLine(line);
                  setSelectedSlotForLine({ day, period });
                  setSelectedClassesForLine(line.class_codes_list || []);
                  setShowLineModal(true);
                }}
              />
            ) : (
              <div className="card text-center py-12">
                <div className="text-neutral-medium-gray text-lg mb-4">No classes found</div>
                <div className="text-neutral-light-gray">
                  Upload classes in the Inputs section to start creating your timetable
                </div>
              </div>
            )}
          </div>

          {/* Right Sidebar - Version Control & Auto-Packing */}
          <div className="w-48 space-y-6">
            {/* Packing Progress by Grade */}
            <div className="card p-4">
              <h3 className="text-sm font-montserrat font-semibold text-northwood-navy mb-3">
                Packing Progress
              </h3>
              <div className="space-y-3">
                {grades.map(grade => {
                  const progress = gradeProgress[grade];
                  const isExpanded = expandedGrades.has(grade);
                  const hasBreakdown = (progress.caps.total > 0 || progress.cam.total > 0);
                  
                  return (
                    <div key={grade}>
                      {/* Main grade row */}
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center space-x-1">
                          {hasBreakdown && (
                            <button
                              onClick={() => {
                                const newExpanded = new Set(expandedGrades);
                                if (isExpanded) {
                                  newExpanded.delete(grade);
                                } else {
                                  newExpanded.add(grade);
                                }
                                setExpandedGrades(newExpanded);
                              }}
                              className="p-0.5 hover:bg-gray-100 rounded transition-colors"
                            >
                              {isExpanded ? (
                                <ChevronUpIcon className="w-3 h-3 text-gray-500" />
                              ) : (
                                <ChevronDownIcon className="w-3 h-3 text-gray-500" />
                              )}
                            </button>
                          )}
                          <span className={`text-xs font-medium ${getGradeTextColor(grade)}`}>
                            Grade {grade}
                          </span>
                        </div>
                        <span className="text-xs text-neutral-medium-gray">
                          {progress.percentage}%
                        </span>
                      </div>
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="text-xs text-neutral-medium-gray">
                          {progress.packed} / {progress.total}
                        </span>
                        <div className="flex-1 bg-neutral-light-gray rounded-full h-1.5">
                          <div 
                            className={`${getGradeBarColor(grade)} h-1.5 rounded-full transition-all duration-300`}
                            style={{ width: `${progress.percentage}%` }}
                          ></div>
                        </div>
                      </div>
                      
                      {/* Accordion breakdown */}
                      {isExpanded && hasBreakdown && (
                        <div className="ml-5 mt-2 space-y-2 border-l-2 border-gray-200 pl-2">
                          {/* CAPS breakdown */}
                          {progress.caps.total > 0 && (
                            <div>
                              <div className="flex items-center justify-between mb-0.5">
                                <span className="text-xs font-medium text-gray-600">
                                  CAPS
                                </span>
                                <span className="text-xs text-neutral-medium-gray">
                                  {progress.caps.percentage}%
                                </span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="text-xs text-neutral-medium-gray">
                                  {progress.caps.packed} / {progress.caps.total}
                                </span>
                                <div className="flex-1 bg-neutral-light-gray rounded-full h-1">
                                  <div 
                                    className="bg-gray-500 h-1 rounded-full transition-all duration-300"
                                    style={{ width: `${progress.caps.percentage}%` }}
                                  ></div>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {/* CAM breakdown */}
                          {progress.cam.total > 0 && (
                            <div>
                              <div className="flex items-center justify-between mb-0.5">
                                <span className="text-xs font-medium text-accent-teal">
                                  CAM
                                </span>
                                <span className="text-xs text-neutral-medium-gray">
                                  {progress.cam.percentage}%
                                </span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="text-xs text-neutral-medium-gray">
                                  {progress.cam.packed} / {progress.cam.total}
                                </span>
                                <div className="flex-1 bg-neutral-light-gray rounded-full h-1">
                                  <div 
                                    className="bg-accent-teal h-1 rounded-full transition-all duration-300"
                                    style={{ width: `${progress.cam.percentage}%` }}
                                  ></div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Version Control */}
            <VersionControl onVersionLoaded={handleVersionLoaded} />

            {/* Auto-Packing for Grade 8 & 9 */}
            <div className="card p-4">
              <h3 className="text-sm font-montserrat font-semibold text-northwood-navy mb-2">
                Auto-Packing
              </h3>
              <p className="text-xs text-neutral-dark-gray mb-3">
                Automatically pack Grade 8 & 9 classes
              </p>
              <button
                onClick={handleOpenAutoPack}
                className="w-full btn-primary hover:bg-opacity-90 transition-all"
              >
                Auto-Pack Grades 8 & 9
              </button>
            </div>
          </div>
        </div>

        {/* Line Packing Modal */}
        {showLineModal && selectedLine && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-montserrat font-semibold text-northwood-navy mb-4">
                Pack Line: {selectedLine.code}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Day {selectedSlotForLine?.day}, Period {selectedSlotForLine?.period}
              </p>
              <p className="text-sm text-gray-600 mb-4">
                Select classes to pack (all must be clash-free):
              </p>
              
              <div className="space-y-2 max-h-60 overflow-y-auto mb-6">
                {selectedLine.class_codes_list && [...selectedLine.class_codes_list].sort().map((classCode: string) => {
                  const classObj = classes.find(c => c.code === classCode);
                  if (!classObj) return null;
                  
                  return (
                    <label key={classCode} className="flex items-center space-x-2 p-2 rounded border hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={selectedClassesForLine.includes(classCode)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedClassesForLine(prev => [...prev, classCode]);
                          } else {
                            setSelectedClassesForLine(prev => prev.filter(c => c !== classCode));
                          }
                        }}
                        className="text-northwood-navy"
                      />
                      <span className="text-sm">
                        {classObj.code} - {classObj.subject_section}
                      </span>
                    </label>
                  );
                })}
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowLineModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleLinePack}
                  disabled={selectedClassesForLine.length === 0}
                  className="flex-1 px-4 py-2 bg-northwood-navy text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Pack {selectedClassesForLine.length} Classes
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Auto-Pack Modal */}
        {showAutoPackModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-montserrat font-semibold text-northwood-navy">
                  Auto-Pack {autoPackSelectedGrades.length === 2 ? 'Grades 8 & 9' : `Grade ${autoPackSelectedGrades[0]}`}
                </h3>
                <button
                  onClick={handleCloseAutoPack}
                  className="text-neutral-medium-gray hover:text-northwood-navy"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Step 1: Info */}
              {autoPackStep === 'info' && (
                <div>
                  <p className="text-sm text-neutral-dark-gray mb-4">
                    Select which grade(s) to pack, review remaining slots, then scan for available solutions.
                  </p>

                  {/* Grade Selection Checkboxes */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                    <h4 className="font-montserrat font-semibold text-northwood-navy mb-3">Select Grades</h4>
                    <div className="space-y-2">
                      <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={autoPackSelectedGrades.includes(8)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setAutoPackSelectedGrades(prev => [...prev, 8].sort());
                            } else {
                              setAutoPackSelectedGrades(prev => prev.filter(g => g !== 8));
                            }
                          }}
                          disabled={autoPackSelectedGrades.length === 1 && autoPackSelectedGrades.includes(8)}
                          className="w-4 h-4 text-northwood-navy border-gray-300 rounded focus:ring-northwood-navy disabled:opacity-50"
                        />
                        <span className="text-sm font-medium text-neutral-dark-gray">Grade 8</span>
                      </label>
                      <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={autoPackSelectedGrades.includes(9)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setAutoPackSelectedGrades(prev => [...prev, 9].sort());
                            } else {
                              setAutoPackSelectedGrades(prev => prev.filter(g => g !== 9));
                            }
                          }}
                          disabled={autoPackSelectedGrades.length === 1 && autoPackSelectedGrades.includes(9)}
                          className="w-4 h-4 text-northwood-navy border-gray-300 rounded focus:ring-northwood-navy disabled:opacity-50"
                        />
                        <span className="text-sm font-medium text-neutral-dark-gray">Grade 9</span>
                      </label>
                    </div>
                    {autoPackSelectedGrades.length === 0 && (
                      <p className="text-xs text-red-600 mt-2">At least one grade must be selected</p>
                    )}
                  </div>
                  
                  {autoPackInfo ? (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                      <h4 className="font-montserrat font-semibold text-northwood-navy mb-3">Remaining Slots</h4>
                      <div className="space-y-2">
                        {autoPackSelectedGrades.includes(8) && (
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-neutral-dark-gray">Grade 8:</span>
                            <span className="text-sm font-semibold text-northwood-navy">
                              {autoPackInfo.grade_8_remaining} slots
                            </span>
                          </div>
                        )}
                        {autoPackSelectedGrades.includes(9) && (
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-neutral-dark-gray">Grade 9:</span>
                            <span className="text-sm font-semibold text-northwood-navy">
                              {autoPackInfo.grade_9_remaining} slots
                            </span>
                          </div>
                        )}
                        {autoPackSelectedGrades.length === 2 && (
                          <div className="border-t border-blue-300 pt-2 mt-2">
                            <div className="flex justify-between items-center">
                              <span className="text-sm font-bold text-neutral-dark-gray">Total:</span>
                              <span className="text-sm font-bold text-northwood-navy">
                                {autoPackInfo.grade_8_remaining + autoPackInfo.grade_9_remaining} slots
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-northwood-navy mx-auto"></div>
                      <p className="text-sm text-neutral-medium-gray mt-2">Loading information...</p>
                    </div>
                  )}

                  {/* Exploration Limit Selector */}
                  <div className="mb-4">
                    <label className="block text-sm font-montserrat font-semibold text-neutral-dark-gray mb-2">
                      Exploration Limit
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="range"
                        min="1000"
                        max="100000"
                        step="1000"
                        value={autoPackMaxExplorations}
                        onChange={(e) => setAutoPackMaxExplorations(Number(e.target.value))}
                        className="flex-1 h-2 bg-neutral-light-gray rounded-lg appearance-none cursor-pointer accent-northwood-navy"
                      />
                      <div className="w-24 text-center">
                        <span className="text-lg font-bold text-northwood-navy">
                          {autoPackMaxExplorations >= 1000 ? `${(autoPackMaxExplorations / 1000).toFixed(0)}k` : autoPackMaxExplorations}
                        </span>
                      </div>
                    </div>
                    <div className="flex justify-between text-xs text-neutral-medium-gray mt-1">
                      <span>Quick (1k)</span>
                      <span>Thorough (100k)</span>
                    </div>
                    <p className="text-xs text-neutral-medium-gray mt-2">
                      Higher limits explore more solution combinations. The algorithm will stop when it finds a complete solution or reaches this limit.
                    </p>
                  </div>

                  <button
                    onClick={handleAutoPackScan}
                    disabled={!autoPackInfo || autoPackSelectedGrades.length === 0}
                    className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Scan for Solutions
                  </button>
                </div>
              )}

              {/* Step 2: Scanning with Progress */}
              {autoPackStep === 'scanning' && (
                <div className="py-8">
                  <div className="text-center mb-6">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-northwood-navy mx-auto mb-4"></div>
                    <p className="text-sm font-medium text-neutral-dark-gray mb-3">
                      Exploring solution space with backtracking algorithm...
                    </p>
                    
                    {/* Timer Display */}
                    <div className="my-4 p-3 bg-northwood-navy text-white rounded-lg">
                      <div className="text-xs font-medium mb-1">Elapsed Time</div>
                      <div className="text-3xl font-bold font-montserrat">
                        {autoPackProgress >= 60 ? `${Math.floor(autoPackProgress / 60)}m ${autoPackProgress % 60}s` : `${autoPackProgress}s`}
                      </div>
                      <div className="text-xs mt-1 text-school-silver">
                        Exploration limit: {autoPackMaxExplorations >= 1000 ? `${(autoPackMaxExplorations / 1000).toFixed(0)}k` : autoPackMaxExplorations} solutions
                      </div>
                    </div>

                    <div className="text-xs text-neutral-medium-gray space-y-1">
                      <div>🔍 Searching through possible slot combinations</div>
                      <div>🧮 Constraint: Max 1 period per class per day</div>
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="text-xs text-blue-700 text-center">
                      <div className="font-semibold mb-1">Algorithm Status</div>
                      <div>Testing valid placements for {autoPackInfo?.periods_to_pack || '...'} class periods</div>
                      <div className="mt-2 text-blue-600">Checking teacher, room, and class clashes...</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Result */}
              {autoPackStep === 'result' && autoPackResult && (
                <div>
                  {autoPackResult.solution_found ? (
                    <div>
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                        <div className="flex items-center mb-2">
                          <svg className="w-6 h-6 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <h4 className="font-montserrat font-semibold text-green-800">Solution Found!</h4>
                        </div>
                        <p className="text-sm text-green-700 mb-2">
                          Successfully found a valid packing solution!
                        </p>
                        {autoPackResult.periods_to_pack && (
                          <div className="text-xs text-green-600 space-y-1 mt-2 border-t border-green-300 pt-2">
                            <div>Classes: {autoPackResult.classes_to_pack}</div>
                            <div className="font-semibold">Periods Packed: {autoPackResult.periods_to_pack}/{autoPackInfo?.periods_to_pack} ({Math.round((autoPackResult.periods_to_pack / autoPackInfo?.periods_to_pack) * 100)}%)</div>
                            <div>Solutions Explored: {autoPackResult.explorations?.toLocaleString()}</div>
                            <div className="font-semibold">Constraint: Max 1 period per class per day</div>
                          </div>
                        )}
                      </div>

                      <div className="flex gap-3">
                        <button
                          onClick={handleCloseAutoPack}
                          className="flex-1 px-4 py-2 border border-neutral-silver rounded-lg font-montserrat font-medium text-neutral-dark-gray hover:bg-neutral-light-gray transition-all"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleApplyAutoPack}
                          className="flex-1 btn-primary"
                        >
                          Apply Solution
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                        <div className="flex items-center mb-2">
                          <svg className="w-6 h-6 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <h4 className="font-montserrat font-semibold text-red-800">No Complete Solution Found</h4>
                        </div>
                        <p className="text-sm text-red-700 mb-2">
                          Unable to find a complete packing solution for all periods.
                        </p>
                        {autoPackResult.message && (
                          <div className="text-xs text-red-600 bg-red-100 rounded p-2 mt-2">
                            <div className="font-semibold mb-1">Best Result:</div>
                            <div>{autoPackResult.message}</div>
                            {autoPackResult.message.match(/\d+\/\d+/) && (() => {
                              const match = autoPackResult.message.match(/(\d+)\/(\d+)/);
                              const packed = parseInt(match[1]);
                              const total = parseInt(match[2]);
                              const percentage = Math.round((packed / total) * 100);
                              return <div className="mt-1 font-semibold">{percentage}% completion with valid slots</div>;
                            })()}
                            <div className="mt-2">Solutions Explored: {autoPackResult.explorations?.toLocaleString()}</div>
                          </div>
                        )}
                      </div>

                      {/* Show Apply Partial button if there's a partial solution */}
                      {autoPackResult.partial_solution && autoPackResult.periods_packed > 0 && (
                        <div className="mb-4">
                          {!showPartialConfirm ? (
                            <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4">
                              <div className="flex items-start mb-2">
                                <svg className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                                <div className="text-sm text-yellow-800">
                                  <div className="font-semibold mb-1">Partial Solution Available</div>
                                  <div>You can apply the best partial solution found ({autoPackResult.periods_packed} periods). This will place valid slots but won't complete the full schedule.</div>
                                </div>
                              </div>
                              <button
                                onClick={() => setShowPartialConfirm(true)}
                                className="w-full mt-3 px-4 py-2 bg-yellow-600 text-white rounded-lg font-montserrat font-medium hover:bg-yellow-700 transition-all"
                              >
                                Apply Partial Solution
                              </button>
                            </div>
                          ) : (
                            <div className="bg-orange-50 border-2 border-orange-400 rounded-lg p-4">
                              <div className="flex items-start mb-3">
                                <svg className="w-6 h-6 text-orange-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                                <div className="text-sm text-orange-900">
                                  <div className="font-bold mb-1">⚠️ Confirm Partial Application</div>
                                  <div>This will add {autoPackResult.periods_packed} periods to your working timetable. The remaining periods will still need manual placement. Are you sure?</div>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => setShowPartialConfirm(false)}
                                  className="flex-1 px-4 py-2 border border-neutral-silver rounded-lg font-montserrat font-medium text-neutral-dark-gray hover:bg-neutral-light-gray transition-all"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={handleApplyPartialSolution}
                                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg font-montserrat font-medium hover:bg-orange-700 transition-all"
                                >
                                  Yes, Apply Partial
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      <button
                        onClick={handleCloseAutoPack}
                        className="w-full btn-primary"
                      >
                        Close
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

      </div>
    </DndProvider>
  );
};

export default TablePackingPage;